import React from 'react'
import { Box, Typography } from '@mui/material'
import Alert from '@mui/material/Alert'
import {
  ResponsiveContainer,
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip as RechartsTooltip,
  Legend
} from 'recharts'
import { PageLayout, PageHeader } from '../../components'
import { LoadingSpinner } from '../../components/atoms'
import useAuthMigrationSummary from '../../hooks/useAuthMigrationSummary'
import styles from './AdminAuthMigration.module.scss'

const ALLOW_COLOR = 'var(--color-success)'
const DENY_COLOR = 'var(--color-error)'
const FALLBACK_COLOR = 'var(--color-warning)'
const DEFAULT_WINDOW_DAYS = 30

const REASON_LABELS = {
  missing_token: 'No token presented',
  invalid_token: 'Invalid token',
  missing_claim: 'Missing authorization claim'
}

const formatRate = (rate) => (rate == null ? '—' : `${(rate * 100).toFixed(1)}%`)

const getSummaryCards = ({ totals, tokenSuccess, users }) => [
  { key: 'total', label: 'Decisions', value: totals.total, color: 'var(--color-deep-blue)' },
  { key: 'wouldAllow', label: 'Would Allow', value: totals.wouldAllow, color: ALLOW_COLOR },
  { key: 'wouldDeny', label: 'Would Deny', value: totals.wouldDeny, color: DENY_COLOR },
  {
    key: 'fallback',
    label: 'Key Fallbacks',
    value: totals.fallback,
    color: FALLBACK_COLOR,
    hint: 'Accept-mode requests that passed on the shared key alone'
  },
  {
    key: 'allowRate',
    label: 'Allow Rate',
    value: formatRate(totals.allowRate),
    color: 'var(--color-deep-blue)'
  },
  {
    key: 'loginSuccess',
    label: 'Login Success Rate',
    value: formatRate(tokenSuccess.rate),
    color: ALLOW_COLOR,
    hint: 'Would-allow share of requests presenting a token'
  },
  { key: 'uniqueDenied', label: 'Users Would Deny', value: users.uniqueDenied, color: DENY_COLOR }
]

const AdminAuthMigration = () => {
  const { summary, meta, loading, error } = useAuthMigrationSummary()

  return (
    <PageLayout>
      <PageHeader
        title='Auth Migration'
        subtitle='What Auth0 token enforcement decides for each API request as routes move up the shadow → accept → enforce ladder.'
      />

      {loading && <LoadingSpinner message='Loading auth decisions...' />}

      {!loading && error && (
        <Alert severity='error' className={styles.errorAlert}>
          Error: {error}
        </Alert>
      )}

      {!loading && !error && summary && (
        <>
          <Typography className={styles.windowNote}>
            Showing the last {meta?.defaultWindowDays ?? DEFAULT_WINDOW_DAYS} days of shadow
            decisions.
          </Typography>

          <Box className={styles.summaryGrid}>
            {getSummaryCards(summary).map(({ key, label, value, color, hint }) => (
              <Box key={key} className={styles.summaryCard} data-testid={`summary-card-${key}`}>
                <Typography className={styles.summaryValue} style={{ color }}>
                  {value}
                </Typography>
                <Typography className={styles.summaryLabel}>{label}</Typography>
                {hint && <Typography className={styles.summaryHint}>{hint}</Typography>}
              </Box>
            ))}
          </Box>

          {summary.totals.total === 0 ? (
            <Alert severity='info' className={styles.emptyAlert}>
              No shadow decisions recorded yet — they appear once token validation
              (FEATURE_AUTH_TOKEN_VALIDATION) is enabled on the backend.
            </Alert>
          ) : (
            <>
              <section className={styles.section}>
                <Typography className={styles.sectionTitle}>Daily Trend</Typography>
                <Box className={styles.chartCard}>
                  <ResponsiveContainer width='100%' height={280}>
                    <LineChart data={summary.byDay}>
                      <CartesianGrid strokeDasharray='3 3' stroke='var(--color-border)' />
                      <XAxis dataKey='day' stroke='var(--color-text-secondary)' />
                      <YAxis allowDecimals={false} stroke='var(--color-text-secondary)' />
                      <RechartsTooltip />
                      <Legend />
                      <Line
                        type='monotone'
                        dataKey='wouldAllow'
                        name='Would Allow'
                        stroke={ALLOW_COLOR}
                        strokeWidth={2}
                        dot={false}
                      />
                      <Line
                        type='monotone'
                        dataKey='wouldDeny'
                        name='Would Deny'
                        stroke={DENY_COLOR}
                        strokeWidth={2}
                        dot={false}
                      />
                      <Line
                        type='monotone'
                        dataKey='fallback'
                        name='Key Fallbacks'
                        stroke={FALLBACK_COLOR}
                        strokeWidth={2}
                        dot={false}
                      />
                    </LineChart>
                  </ResponsiveContainer>
                </Box>
              </section>

              <section className={styles.section}>
                <Typography className={styles.sectionTitle}>Would-Deny by Route</Typography>
                <div className={styles.tableWrapper}>
                  <table className={styles.table}>
                    <thead>
                      <tr>
                        <th>Method</th>
                        <th>Route</th>
                        <th>Decisions</th>
                        <th>Would Deny</th>
                        <th>Key Fallbacks</th>
                        <th>Deny Rate</th>
                      </tr>
                    </thead>
                    <tbody>
                      {summary.byRoute.map((row) => (
                        <tr key={`${row.method} ${row.route}`}>
                          <td className={styles.monoCell}>{row.method}</td>
                          <td className={styles.monoCell}>{row.route}</td>
                          <td>{row.total}</td>
                          <td style={{ color: row.wouldDeny > 0 ? DENY_COLOR : undefined }}>
                            {row.wouldDeny}
                          </td>
                          <td style={{ color: row.fallback > 0 ? FALLBACK_COLOR : undefined }}>
                            {row.fallback}
                          </td>
                          <td>{formatRate(row.wouldDeny / row.total)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </section>

              <section className={styles.section}>
                <Typography className={styles.sectionTitle}>Deny Reasons</Typography>
                <div className={styles.tableWrapper}>
                  <table className={styles.table}>
                    <thead>
                      <tr>
                        <th>Reason</th>
                        <th>Count</th>
                      </tr>
                    </thead>
                    <tbody>
                      {summary.byReason.map((row) => (
                        <tr key={row.reason}>
                          <td>{REASON_LABELS[row.reason] || row.reason}</td>
                          <td>{row.count}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </section>
            </>
          )}
        </>
      )}
    </PageLayout>
  )
}

export default AdminAuthMigration
